import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PerfilOptionStruct } from '../../services/structs/usuario/perfil-option.struct';

/**
 * Lista editável de perfis, sem valor por linha: "Adicionar" insere uma linha (perfil + remover),
 * com estado vazio. Espelha o app-desconto-maximo-perfil sem o campo de percentual.
 *
 * Usado pela permissão de alteração de preço por perfil das Configurações Gerais de Vendas, que pede o
 * mesmo padrão visual do bloco de desconto por perfil. O PDV resolve dado parecido
 * (`listIdPerfilAutoatendimento`) com um mat-select multiple, que não é o padrão desta tela.
 */
@Component({
  selector: 'app-perfil-autorizado-lista',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './perfil-autorizado-lista.component.html',
  styleUrls: ['./perfil-autorizado-lista.component.css'],
})
export class PerfilAutorizadoListaComponent implements OnChanges {
  @Input() perfis: PerfilOptionStruct[] = [];
  @Input() value: number[] = [];
  @Input() disabled: boolean = false;
  @Input() titulo: string = '';
  /** Quando preenchido, exibe o ícone `info` com este texto em tooltip ao lado do título. */
  @Input() tooltip: string = '';
  @Input() textoVazio: string = 'Nenhum perfil na lista';
  @Input() textoVazioApoio: string = '';
  @Output() valueChange = new EventEmitter<number[]>();

  public items: (number | null)[] = [];
  private internalUpdate = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      // Ignora o eco da própria emissão para não descartar a linha em edição (perfil ainda não escolhido).
      if (this.internalUpdate) {
        this.internalUpdate = false;
        return;
      }
      this.items = [...(this.value || [])];
    }
  }

  public get podeAdicionar(): boolean {
    return !this.disabled && this.items.length < this.perfis.length;
  }

  public get tooltipAdicionar(): string {
    if (this.perfis.length === 0) return 'Nenhum perfil cadastrado no sistema.';
    if (this.items.length >= this.perfis.length) return 'Todos os perfis já foram adicionados';
    return 'Adicionar perfil';
  }

  public perfisDisponiveis(rowIndex: number): PerfilOptionStruct[] {
    return this.perfis.filter(p => !this.items.some((id, j) => j !== rowIndex && id === p.idPerfil));
  }

  public adicionar(): void {
    if (!this.podeAdicionar) return;
    this.items = [...this.items, null];
  }

  public onPerfilChange(index: number, idPerfil: number): void {
    this.items[index] = idPerfil;
    this.emit();
  }

  public remover(index: number): void {
    this.items.splice(index, 1);
    this.emit();
  }

  private emit(): void {
    this.internalUpdate = true;
    this.valueChange.emit(this.items.filter((id): id is number => id != null));
  }
}
